import { cn } from '../../lib/cn.js'
import { Text } from '../ui/Typography.js'

export interface FormSectionProps {
  /** Three or four words. "When and how long", not "Scheduling information". */
  title: React.ReactNode
  /** One line, only where the group needs one. Most do not. */
  hint?: React.ReactNode
  className?: string
  children: React.ReactNode
}

/**
 * A group of fields that belong together, under a heading.
 *
 * The editors were one `Card` holding seventeen fields in a single column with
 * the same gap between every pair of them, so "what it is called" and "where it
 * is" and "who to pay" all read as one undifferentiated list and the only way
 * to find a field was to scroll past the others. Nothing about the form said
 * where one subject ended.
 *
 * A section is a heading, a hairline, and its fields. The heading is the small
 * uppercase eyebrow rather than a card title, because these sit *inside* a
 * sheet that already has a title — a card title here competes with the name of
 * the thing being edited.
 *
 * There is deliberately no border and no card. A sheet is already a surface;
 * putting a card inside it draws a box inside a box, which is the "double
 * border double layer" the client called out on the tables.
 */
export function FormSection({ title, hint, className, children }: FormSectionProps) {
  return (
    <section className={cn('flex flex-col gap-4', className)}>
      <div className="border-hairline flex flex-col gap-1 border-b pb-2">
        <Text as="h3" variant="eyebrow" className="text-ink-muted">
          {title}
        </Text>
        {hint && <Text variant="caption">{hint}</Text>}
      </div>
      {children}
    </section>
  )
}

/**
 * A switch on its own line, in a box, with the label beside it.
 *
 * `Field` puts its label *above* its control, which is right for a text box and
 * wrong for a switch: "Outdoors" on one line with a toggle underneath it reads
 * as a heading over an unlabelled control. A switch wants its words on the left
 * and itself on the right, on one line, and a border so it does not float
 * between two text fields.
 */
export function SwitchField({
  label,
  hint,
  children,
  className,
}: {
  label: React.ReactNode
  hint?: React.ReactNode
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      className={cn(
        'border-hairline bg-surface flex items-center justify-between gap-4 rounded-md border px-4 py-3',
        className,
      )}
    >
      <span className="flex min-w-0 flex-col gap-0.5">
        <Text as="span" variant="body">
          {label}
        </Text>
        {hint && (
          <Text as="span" variant="caption">
            {hint}
          </Text>
        )}
      </span>
      <span className="shrink-0">{children}</span>
    </div>
  )
}
